import AbstractSessionModel from '../../../models/AbstractSessionModel';
import Observable from '../../../decorators/Observable';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import Action from '../../../decorators/Action';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import WizardSessionData from '../../../services/WizardSessionData';
import Api from '../../../services/Api';
import SocialMediaWizardDataModel from './SocialMediaWizardDataModel';

interface SocialMediaProfileData {
  url: string;
}

class SocialMediaModel
  extends AbstractSessionModel
  implements IWizardDataModel
{
  @Observable()
  @StoreFieldInSession({
    type: 'Map'
  })
  public socialMediaProfiles: Map<string, SocialMediaProfileData> = new Map();

  @Observable()
  public isLoadingSocialProfiles: boolean = false;

  @Observable()
  public socialProfilesError: string | null = null;

  @Observable()
  public socialMediaWizardDataModel = new SocialMediaWizardDataModel();

  private currentRequestAbortController: AbortController | null = null;
  private currentRequestParams: {
    location: string;
    businessName: string;
    website: string;
  } | null = null;

  constructor() {
    super();
    super.initFields();
  }

  @Action()
  async loadSocialMediaProfiles(params: {
    location: string;
    businessName: string;
    website: string;
  }) {
    const currentParamsStr = JSON.stringify(params);

    if (
      this.isLoadingSocialProfiles &&
      this.currentRequestParams &&
      JSON.stringify(this.currentRequestParams) === currentParamsStr
    ) {
      // Another request with the same parameters is already in progress
      return;
    }

    if (this.isLoadingSocialProfiles && this.currentRequestAbortController) {
      // Another request with different parameters is in progress, cancel it
      this.currentRequestAbortController.abort(
        new DOMException('Cancelled by new request', 'Cancellation')
      );
    }

    this.isLoadingSocialProfiles = true;
    this.socialProfilesError = null;

    // This controller is for the new request, to be aborted by a subsequent request
    const manualAbortController = new AbortController();
    this.currentRequestAbortController = manualAbortController;
    this.currentRequestParams = params; // Store params for the new request

    const timeoutSignal = AbortSignal.timeout(60000); // 60 seconds timeout

    // Create a new AbortController that will be aborted if either manual or timeout signal aborts.
    const linkedAbortController = new AbortController();
    const combinedSignal = linkedAbortController.signal;

    let onManualAbortCalled = false;
    let onTimeoutCalled = false;

    const cleanupEventListeners = () => {
      manualAbortController.signal.removeEventListener('abort', onManualAbort);
      timeoutSignal.removeEventListener('abort', onTimeout);
    };

    const onManualAbort = () => {
      if (onManualAbortCalled) return;
      onManualAbortCalled = true;
      console.log(
        'Request cancelled by new request:',
        params,
        manualAbortController.signal.reason
      );
      if (!linkedAbortController.signal.aborted) {
        linkedAbortController.abort(manualAbortController.signal.reason);
      }
      cleanupEventListeners();
    };

    const onTimeout = () => {
      if (onTimeoutCalled) return;
      onTimeoutCalled = true;
      console.log('Request timed out:', params, timeoutSignal.reason);
      if (!linkedAbortController.signal.aborted) {
        linkedAbortController.abort(timeoutSignal.reason);
      }
      cleanupEventListeners();
    };

    manualAbortController.signal.addEventListener('abort', onManualAbort, {
      once: true
    });
    timeoutSignal.addEventListener('abort', onTimeout, { once: true });

    try {
      const response = await Api.scrapeSocialMediaProfiles(
        params,
        combinedSignal
      );

      // If execution reaches here, the request was not aborted before API call returned.
      // Cleanup listeners if they haven't been triggered by an abort event.
      if (!combinedSignal.aborted) {
        cleanupEventListeners();
      }

      // If the signal aborted during or very shortly after the API call, but before this check
      if (combinedSignal.aborted) {
        // The reason for abort (timeout or cancellation) would have been handled by the catch block
        // or by the event listeners setting the state.
        // If timeout, profiles are set to empty.
        console.log(
          'Request processing skipped as signal was aborted post-API call:',
          combinedSignal.reason
        );
        // Ensure profiles are empty if it was a timeout not caught by the main catch block
        if (timeoutSignal.aborted) {
          this.socialMediaProfiles = new Map();
          this.socialProfilesError = null;
        }
        return;
      }

      if (response.status === 'success' && response.data) {
        const profilesMap = new Map<string, SocialMediaProfileData>();
        Object.entries(response.data).forEach(([platform, url]) => {
          if (url && typeof url === 'string' && url.trim() !== '') {
            profilesMap.set(platform, { url: url as string });
          }
        });
        this.socialMediaProfiles = profilesMap;
        this.socialProfilesError = null;
      } else {
        this.socialProfilesError =
          response.status === 'error'
            ? 'Failed to load social media profiles'
            : 'No social media profiles found';
        this.socialMediaProfiles = new Map(); // Set to empty on error/no data
      }
    } catch (error) {
      // Ensure listeners are cleaned up if an error occurs.
      cleanupEventListeners();

      if (combinedSignal.aborted) {
        const reason = combinedSignal.reason;
        if (reason instanceof DOMException && reason.name === 'TimeoutError') {
          console.log('Request timed out (in catch):', params);
          this.socialMediaProfiles = new Map(); // Set to empty map on timeout
          this.socialProfilesError = null;
        } else if (
          reason instanceof DOMException &&
          reason.name === 'Cancellation'
        ) {
          console.log('Request cancelled by a new request (in catch):', params);
          // State will be handled by the new request.
        } else {
          console.log(
            'Request aborted for other reason (in catch):',
            params,
            reason
          );
          this.socialProfilesError = 'Request was aborted.';
          this.socialMediaProfiles = new Map();
        }
      } else {
        // Handle actual API errors (not aborts)
        console.error('API Error for social media profiles:', error);
        this.socialProfilesError =
          error instanceof Error ? error.message : 'Unknown error occurred';
        this.socialMediaProfiles = new Map(); // Clear profiles on other errors too
      }
    } finally {
      // Only reset loading state if this request (identified by `currentParamsStr`) is still the "current" one.
      if (JSON.stringify(this.currentRequestParams) === currentParamsStr) {
        this.isLoadingSocialProfiles = false;
        // If the current controller is the one for this finished/failed request, nullify it.
        if (this.currentRequestAbortController === manualAbortController) {
          this.currentRequestAbortController = null;
          this.currentRequestParams = null;
        }
      }
      // Ensure listeners are cleaned up in all paths.
      cleanupEventListeners();
    }
  }

  @Action()
  setSocialMediaProfiles(profiles: Map<string, SocialMediaProfileData>) {
    this.socialMediaProfiles = profiles;
  }

  @Action()
  addSocialMediaProfile(platform: string, profileData: SocialMediaProfileData) {
    const newMap = new Map(this.socialMediaProfiles);
    newMap.set(platform, profileData);
    this.socialMediaProfiles = newMap;
  }

  @Action()
  removeSocialMediaProfile(platform: string) {
    const newMap = new Map(this.socialMediaProfiles);
    newMap.delete(platform);
    this.socialMediaProfiles = newMap;
  }

  @Action()
  updateSocialMediaProfile(
    platform: string,
    updatedProfile: Partial<SocialMediaProfileData>
  ) {
    const currentProfile = this.socialMediaProfiles.get(platform);
    if (currentProfile) {
      const newMap = new Map(this.socialMediaProfiles);
      newMap.set(platform, { ...currentProfile, ...updatedProfile });
      this.socialMediaProfiles = newMap;
    }
  }

  /**
   * Get a social media profile by platform name
   */
  getSocialMediaProfileByPlatform(
    platform: string
  ): SocialMediaProfileData | undefined {
    return this.socialMediaProfiles.get(platform);
  }

  /**
   * Check if a platform already exists in the profiles
   */
  hasPlatform(platform: string): boolean {
    return this.socialMediaProfiles.has(platform);
  }

  /**
   * Get all platform names currently stored
   */
  getAllPlatformNames(): string[] {
    return Array.from(this.socialMediaProfiles.keys());
  }

  /**
   * Get all profiles as an array of entries [platform, data]
   */
  getAllProfilesAsArray(): Array<[string, SocialMediaProfileData]> {
    return Array.from(this.socialMediaProfiles.entries());
  }

  /**
   * Clear all social media profiles
   */
  @Action()
  clearSocialMediaProfiles() {
    this.socialMediaProfiles = new Map();
    this.socialProfilesError = null;
  }

  getWizardData() {
    return this.socialMediaWizardDataModel.getWizardData();
  }

  public getSessionKey(): string {
    return 'SocialMediaModel';
  }
}

const model = new SocialMediaModel();
WizardSessionData.addModel(model);
export default model;
